import { z } from 'zod';
import { chartTypeIconSchema } from './chartTypeIcons';
import { isValidDataAppSlug } from './code';
import { dataAppVizSchema } from './types';
export const CHART_REGISTRY_INDEX_SCHEMA_VERSION = 1;
const SEMVER_VERSION_RE = /^\d+\.\d+\.\d+$/;
export const isSemverVersion = (value) => SEMVER_VERSION_RE.test(value);
/** Strict x.y.z numeric compare; throws on anything else. */
export const compareSemverVersions = (left, right) => {
    if (!isSemverVersion(left) || !isSemverVersion(right)) {
        throw new Error(`Cannot compare non-semver versions: ${left} vs ${right}`);
    }
    const l = left.split('.').map(Number);
    const r = right.split('.').map(Number);
    for (let i = 0; i < 3; i += 1) {
        if (l[i] !== r[i])
            return l[i] < r[i] ? -1 : 1;
    }
    return 0;
};
const semverString = z
    .string()
    .refine(isSemverVersion, { message: 'must be a strict x.y.z version' });
const registrySlug = z
    .string()
    .refine(isValidDataAppSlug, { message: 'must be a valid chart type slug' });
const registryArtifactSchema = z.object({
    path: z.string().min(1),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
const registryEntrySchema = z.object({
    slug: registrySlug,
    name: z.string().min(1),
    description: z.string(),
    version: semverString,
    publishedAt: z.string(),
    tags: z.array(z.string()).default([]),
    changelog: z.string().default(''),
    minLightdashVersion: semverString.nullable().default(null),
    channel: z.enum(['stable', 'beta']).optional(),
    vizSchema: dataAppVizSchema,
    thumbnail: z.string().nullable().default(null),
    thumbnailDark: z.string().nullable().default(null),
    screenshots: z.array(z.string()).default([]),
    icon: chartTypeIconSchema.nullable().default(null),
    artifacts: z.object({
        source: registryArtifactSchema,
        dist: registryArtifactSchema,
    }),
});
export const chartRegistryIndexSchema = z.object({
    schemaVersion: z.literal(CHART_REGISTRY_INDEX_SCHEMA_VERSION),
    generatedAt: z.string(),
    charts: z.array(registryEntrySchema),
});
const chartRegistryIndexEnvelopeSchema = z.object({
    schemaVersion: z.literal(CHART_REGISTRY_INDEX_SCHEMA_VERSION),
    generatedAt: z.string(),
    charts: z.array(z.unknown()),
});
/**
 * Parses a registry index, dropping entries that fail validation instead of
 * failing the whole index. The registry evolves independently of deployed
 * instances — one entry with, say, a channel value this build doesn't know
 * must hide that chart, not blank the entire library. An invalid envelope
 * (wrong schemaVersion, charts not an array) still throws: that's an
 * unusable registry, not a forward-compat entry.
 */
export const parseChartRegistryIndexTolerant = (raw) => {
    const envelope = chartRegistryIndexEnvelopeSchema.parse(raw);
    const charts = [];
    const dropped = [];
    for (const rawEntry of envelope.charts) {
        const parsed = registryEntrySchema.safeParse(rawEntry);
        if (parsed.success) {
            charts.push(parsed.data);
        }
        else {
            const slug = typeof rawEntry === 'object' &&
                rawEntry !== null &&
                'slug' in rawEntry &&
                typeof rawEntry.slug === 'string'
                ? rawEntry.slug
                : null;
            dropped.push({
                slug,
                message: parsed.error.issues
                    .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
                    .join('; '),
            });
        }
    }
    return {
        index: {
            schemaVersion: envelope.schemaVersion,
            generatedAt: envelope.generatedAt,
            charts,
        },
        dropped,
    };
};
const chartRegistryIndexSchemaMatchesApiType = true;
void chartRegistryIndexSchemaMatchesApiType;
//# sourceMappingURL=registry.js.map